// Волна 32, блок «логистика» (зонтичный гейт isPreW32LogisticsPlayer и подгейты
// L3-L5, abTestGates.js). Раунд 5: коридор дорог L1 (calcForwardCorridorProximity,
// isCorridorProfileEligible, W32_CORRIDOR_*) удален — A/B r4: −0.59 очка.
// Чистые функции и константы блока. Дистанции — BFS в ячейках (calcBfsDistances).
// «Дистанцию в ходах» сознательно не вводим: веса перемещения и стройки
// откалиброваны в ячейках (волны 18-28)
import {FINAL_ROUND} from "../../../configs/game";

// ---- L3: марш захватной кампании через разрыв снабжения (перемещение) ----
// Штраф ОБЯЗАН быть < CAMPAIGN_APPROACH_PER_CELL (5) − штраф пустой ячейки (2,
// decideMovementStep, волна 22 21.16): край конверта снабжения — непустая ячейка
// глубины 5 от фермы, шаг с нее на пустую ячейку за конвертом = +5 −2 −штраф,
// а ход требует прирост строго > 0. При 1: +2 с дороги, +4 с пустой ячейки;
// откат в снабжение остается невыгодным
export const W32_CAMPAIGN_MARCH_SUPPLY_PENALTY = 1;
// Остаток пути от края конверта (5 непустых шагов от фермы) до кварталов соседа
export const W32_MARCH_MAX_TARGET_DISTANCE = 4;
export const W32_MARCH_STARVATION_PER_ROUND = 2;
// Пустой город захватывает отряд любого размера, но размер 1 погибнет от первого голода
export const W32_MARCH_MIN_ARRIVAL_SIZE = 2;
// Охрану надо выбить: ожидаемый урон отряда = размер − 2
export const W32_MARCH_GARRISON_MARGIN = 2;

// Размер по прибытии, нужный для захвата: пустой город — 2 (переживет первый голод),
// охраняемый — гарнизон + 2 (ожидаемый урон = размер − 2). Ее же использует порядок
// целей L5 (orderCaptureTargetsByReach).
// Волна 32, раунд 4: тюнинг модели марша T (дистанция до цели 3, отряд сохраняет
// половину размера) удален — A/B r3: −1.3 п.п. винрейта; модель марша — r2
export const calcMarchRequiredArrivalSize = (targetGarrison = 0) => (targetGarrison > 0
  ? targetGarrison + W32_MARCH_GARRISON_MARGIN
  : W32_MARCH_MIN_ARRIVAL_SIZE);

/**
 * Можно ли отряду захватной кампании встать на ячейку вне снабжения.
 * Оценка пессимистичная: 1 ячейка за раунд (вход в пустую ячейку завершает ход)
 * и −2 размера за каждую оставшуюся в партии фазу снабжения на пути.
 * Ячейка должна быть не дальше от цели, чем текущая ячейка отряда, и достижима
 * до конца партии по той же модели: отряд, вставший в раунде r на ячейку с
 * дистанцией d, оказывается на квартале в конце раунда r+d (захват проверяется
 * в конце раунда).
 * При продвижении на 1 ячейку за раунд величина size − 2·d не меняется, поэтому
 * идущий отряд разрешения не теряет. Стоящий теряет его и получает тягу обратно
 * в снабжение (W32_SUPPLY_RETURN_PER_CELL в перемещении).
 * Упущенный рост (+1 в снабжении) модель не учитывает: порог отсекает выживание,
 * а не выгоду.
 */
export const isSupplyGapMarchAllowed = ({
  unitSize,
  distanceFromCell,
  distanceFromUnit,
  targetGarrison = 0,
  round,
  finalRound = FINAL_ROUND,
}) => {
  if (!Number.isFinite(distanceFromCell) || !Number.isFinite(distanceFromUnit)) {
    return false;
  }
  if (distanceFromCell > distanceFromUnit || distanceFromCell > W32_MARCH_MAX_TARGET_DISTANCE) {
    return false;
  }
  // Цель не достигается до конца партии — голод без захвата
  if (Number.isFinite(round) && distanceFromCell > Math.max(0, finalRound - round)) {
    return false;
  }
  // Фазы снабжения идут в начале раундов round+1..finalRound; после проверки
  // выше их на пути не меньше дистанции, так что голодных фаз ровно d
  const arrivalSize = unitSize - W32_MARCH_STARVATION_PER_ROUND * distanceFromCell;
  return arrivalSize >= calcMarchRequiredArrivalSize(targetGarrison);
};

// ---- L3: возврат в снабжение отряда, потерявшего разрешение марша (перемещение) ----
// Вес за ячейку BFS до конверта снабжения. Больше суммы тяг, которые держат отряд
// без кампании вне снабжения: фронтир 6 × до 2 у бонусного бота + общий градиент 2
// + давление 1.2, и больше градиента кампании 5. Разрешенные ячейки марша идут
// своей веткой (−1) и эту тягу не получают, так что шаг марша вперед не ломается
export const W32_SUPPLY_RETURN_PER_CELL = 16;
// Глубина BFS от конверта; ячейки дальше получают дистанцию MAX + 1
export const W32_SUPPLY_RETURN_MAX_DISTANCE = 8;

// Ожидание роста ради разрешения марша: следующая ячейка к цели (d − 1) лежит
// вне конверта, марш на нее сейчас не разрешен, а при максимальном размере отряда
// будет разрешен. Если не поможет и максимум — ожидание бессмысленно, таймер L4 идет
export const isWaitingForMarchGrowth = ({
  unitSize,
  maxSize,
  distanceFromUnit,
  hasOutOfSupplyStepCloser,
  targetGarrison = 0,
  round,
  finalRound = FINAL_ROUND,
}) => {
  if (!hasOutOfSupplyStepCloser || !Number.isFinite(distanceFromUnit) || distanceFromUnit < 1) {
    return false;
  }
  const march = (size) => isSupplyGapMarchAllowed({
    unitSize: size,
    distanceFromCell: distanceFromUnit - 1,
    distanceFromUnit,
    targetGarrison,
    round,
    finalRound,
  });
  return !march(unitSize) && maxSize > unitSize && march(maxSize);
};

// ---- L4: контроль прогресса захватных кампаний ----
// Условие «round − progressRound >= 3» проверяется в начале этапа перемещения,
// то есть кампанию снимают после двух полных фаз войны без сокращения дистанции
export const W32_CAMPAIGN_MAX_STALL_ROUNDS = 3;
export const W32_CAMPAIGN_STALL_BLACKLIST_ROUNDS = 3;

/**
 * Прогресс кампании по лучшей BFS-дистанции отряда до цели: отход назад
 * прогрессом не считается, отряд на самой цели (дистанция 0) не застревает
 */
export const evaluateCampaignProgress = ({
  bestDistance, progressRound, currentDistance, round, maxStallRounds = W32_CAMPAIGN_MAX_STALL_ROUNDS,
}) => {
  if (!Number.isFinite(currentDistance)) {
    return {bestDistance, progressRound, isStalled: false};
  }
  if (!Number.isFinite(bestDistance) || !Number.isFinite(progressRound) || currentDistance < bestDistance) {
    return {bestDistance: currentDistance, progressRound: round, isStalled: false};
  }
  return {
    bestDistance,
    progressRound,
    // Отряд на самой цели (дистанция 0) ждет конца раунда — это не застревание
    isStalled: currentDistance >= 1 && round - progressRound >= maxStallRounds,
  };
};

/**
 * Сбросить таймер застревания на время ожидания роста (удержание на казарме или
 * у края снабжения ради марша). Ожидание засчитывается только пока отряд растет:
 * первое наблюдение и повторные тики того же раунда сбрасывают таймер, в следующих
 * раундах — только если размер вырос с прошлого сброса. Отряд, который не растет
 * (нет снабжения по бюджету ферм, потери в бою), копит застревание как обычно
 */
export const checkIfGrowthWaitResetsStall = ({unitSize, waitSize, waitRound, round}) =>
  !Number.isFinite(waitSize) || waitRound === round || unitSize > waitSize;

// Последний раунд блокировки включительно: N раундов, начиная с текущего
export const calcStallBlacklistUntilRound = (round, blacklistRounds = W32_CAMPAIGN_STALL_BLACKLIST_ROUNDS) =>
  round + Math.max(1, blacklistRounds) - 1;

// stalls: Map<unitId, {cells: string[], untilRound: number}>
export const isCampaignTargetBlacklisted = (stalls, unitId, targetHexKey, round) => {
  const stall = stalls && stalls.get(unitId);
  return Boolean(stall && stall.untilRound >= round && stall.cells.includes(targetHexKey));
};

// ---- L5: порядок захватных целей ----
export const W32_CAPTURE_GARRISON_DISTANCE_EQUIVALENT = 0.5; // 1 размер гарнизона = 0.5 ячейки пути
// Цель, чей гарнизон не выбьет даже крупнейший подходящий свободный отряд
// (та же модель, что у марша L3), — после всех посильных
export const W32_CAPTURE_INFEASIBLE_KEY_PENALTY = 1000;

/**
 * Порядок захватных целей: дистанция от ближайшего подходящего отряда +
 * гарнизон (0.5 ячейки за размер) + штраф непосильной цели. При равенстве —
 * прежний порядок (по гарнизону), недостижимые — в конце
 */
export const orderCaptureTargetsByReach = (
  targetHexKeys,
  distancesFromUnits,
  garrisonByTarget = null,
  maxAttackerSize = Infinity,
  garrisonWeight = W32_CAPTURE_GARRISON_DISTANCE_EQUIVALENT,
) => {
  const keyed = targetHexKeys.map((hexKey, index) => {
    const distance = distancesFromUnits.get(hexKey);
    const garrison = (garrisonByTarget && garrisonByTarget.get(hexKey)) || 0;
    const infeasiblePenalty = garrison > 0 && calcMarchRequiredArrivalSize(garrison) > maxAttackerSize
      ? W32_CAPTURE_INFEASIBLE_KEY_PENALTY
      : 0;
    return {
      hexKey,
      index,
      key: (Number.isFinite(distance) ? distance : Infinity) + garrison * garrisonWeight + infeasiblePenalty,
    };
  });
  keyed.sort((a, b) => {
    if (a.key !== b.key) {
      return a.key < b.key ? -1 : 1;
    }
    return a.index - b.index; // при равенстве — прежний порядок (по гарнизону)
  });
  return keyed.map(({hexKey}) => hexKey);
};
